import { TranslationUtils } from '../../lang/i18n';
import {Component, h, Prop, Watch} from '@stencil/core';

/**
 * Interface for installment data
 */
export interface Installment {
    amount: string;
    isCompleted?: boolean;
    progressType?: 'empty' | 'small' | 'medium' | 'full';
    label?: string;
}

/**
 * Timeline display style options
 */
export type TimelineStyle = 'default' | 'simple';

/**
 * Label format options (deprecated - kept for backward compatibility)
 */
export type LabelFormat = 'date' | 'simple';

@Component({
    tag: 'amwal-installments-timeline',
    styleUrl: 'amwal-installments-timeline.css',
    shadow: false,
})
export class AmwalInstallmentsTimeline {

    /**
     * The total amount to be paid in installments
     */
    @Prop() amount: number = 0;

    /**
     * Number of installments
     */
    @Prop() installmentsCount: number = 6;

    /**
     * The installment payments (optional - will be calculated if not provided)
     */
    @Prop() installmentData: string = '[]';

    /**
     * Enable or disable installments feature
     */
    @Prop() enableInstallments: boolean = false;

    /**
     * Maximum number of installments to display (for UI clarity)
     */
    @Prop() maxVisibleInstallments: number = 3;

    /**
     * the language of the plugin. supported languages are Arabic ("ar") and English ("en")
     */
    @Prop() locale?: string = "en";

    /**
     * Enable or disable border around the timeline
     */
    @Prop() border: boolean = false;

    /**
     * Timeline display style: 'default' for circular timeline, 'simple' for modern dots with lines
     */
    @Prop() timelineStyle: TimelineStyle = 'default';

    /**
     * Footer message for simple timeline style
     */
    @Prop() footerMessage?: string;

    /**
     * Enable debug mode for additional console logs
     * @default false
     */
    @Prop() debug: boolean = false;

    /**
     * Number of decimal places for amount formatting
     * @default 2
     * @minimum 0
     * @maximum 6
     */
    @Prop() decimalPlaces: number = 2;

    // Deprecated props - kept for backward compatibility
    @Prop() labelFormat: LabelFormat = 'simple';
    @Prop() startDate?: string;
    @Prop() intervalMonths: number = 1;

    /**
     * Translation function to handle localization
     * @private
     */
    private t: (val: string) => string = (val) => val; // Initialize with fallback

    /**
     * Lifecycle method that is called once when the component is first created
     */
    async componentWillLoad() {
        try {
            this.t = await TranslationUtils.useTranslation(this.locale);
        } catch (e) {
            console.error('Failed to initialize translation:', e);
        }
    }

    /**
     * Lifecycle method to update translations when locale changes
     */
    @Watch('locale')
    async updateLocale(newValue: string) {
        this.locale = newValue;
        try {
            this.t = await TranslationUtils.useTranslation(this.locale);
        } catch (e) {
            console.error('Failed to update translation:', e);
            this.t = (val) => val;
        }
    }

    /**
     * Generate simple label for installment (1-based indexing)
     */
    private generateSimpleLabel(index: number): string {
        return this.t('Installment') + ' ' + (index + 1);
    }

    /**
     * Get installment data as parsed object or generate it dynamically
     */
    private get installments(): Installment[] {
        try {
            const providedData = JSON.parse(this.installmentData);
            if (providedData.length > 0) {
                return providedData;
            }
            return this.generateInstallments();
        } catch (e) {
            console.error('Invalid installment data format', e);
            return this.generateInstallments();
        }
    }

    /**
     * Generate installment data based on amount and installmentsCount
     */
    private generateInstallments(): Installment[] {
        const installments: Installment[] = [];
        if (this.amount <= 0 || this.installmentsCount <= 0) {
            return installments;
        }

        // Calculate the base installment amount
        const baseAmount = +(this.amount / this.installmentsCount).toFixed(this.decimalPlaces);
        const remainder = +(this.amount - (baseAmount * this.installmentsCount)).toFixed(this.decimalPlaces);

        // Create installment objects
        for (let i = 0; i < this.installmentsCount; i++) {
            // Add remainder to the last installment if needed
            const amount = i === this.installmentsCount - 1 && remainder !== 0
                ? (baseAmount + remainder).toFixed(this.decimalPlaces)
                : baseAmount.toFixed(this.decimalPlaces);

            // Assign progressive status based on position
            let progressType: 'empty' | 'small' | 'medium' | 'full' = 'empty';
            if (i === 0) {
                progressType = 'small';
            } else if (i === 1 || (i > 1 && i < this.installmentsCount - 1)) {
                progressType = 'medium';
            } else if (i === this.installmentsCount - 1) {
                progressType = 'full';
            }

            const installment: Installment = {
                amount: `${amount}`,
                progressType,
                label: this.generateSimpleLabel(i)
            };

            installments.push(installment);
        }

        return installments;
    }

    /**
     * Format amount to display
     */
    private formatAmount(amount: string, label?: string): any {
        const amountParts = amount.split(' ');
        const numericValue = parseFloat(amountParts[0]).toFixed(this.decimalPlaces);

        return (
            <div class="installments-amount">
                <div class="amount-container">
                    {label && <span class="installments-label">{label}</span>}
                    <span class="installments-amount-value">{numericValue}</span>
                </div>
            </div>
        );
    }

    /**
     * Render an installment step (default circular style)
     */
    private renderInstallmentStep(
        amount: string,
        label: string,
        isFirst: boolean = false,
        isLast: boolean = false,
        progressType: 'empty' | 'small' | 'medium' | 'full' = 'empty'
    ) {
        return (
            <div class="installments-step">
                {isFirst
                    ? this.formatAmount(amount, this.t('Hold'))
                    : this.formatAmount(amount)
                }
                <div class="installments-circle-container">
                    {isFirst ? (
                        <div class="installments-circle-filled-first"></div>
                    ) : isLast || progressType === 'full' ? (
                        <div class="installments-circle-filled"></div>
                    ) : (
                        [
                            <div class="installments-circle-outline"></div>,
                            progressType === 'small' && (
                                <div class="installments-circle-segment installments-circle-segment-small"></div>
                            ),
                            progressType === 'medium' && (
                                <div class="installments-circle-segment installments-circle-segment-medium"></div>
                            )
                        ]
                    )}
                </div>
                <div class="installments-label">{label}</div>
            </div>
        );
    }

    /**
     * Render a modern timeline step with new dot style and connecting lines
     */
    private renderModernTimelineStep(installment: Installment, isHold: boolean = false) {
        const progressClass = installment.progressType || 'empty';
        // Generate short version for mobile displays
        const shortLabel = installment.label?.replace('Installment', 'Inst') || installment.label;

        return (
            <div class="date-timeline-step">
                <div class={`modern-timeline-dot ${progressClass}`}></div>
                <div class="date-timeline-content">
                    <div class="date-timeline-date" data-short={shortLabel}>{installment.label}</div>
                    <div class="date-timeline-amount">
                        {isHold && (
                            <span class="date-timeline-hold-label">{this.t('Hold')}</span>
                        )}
                        {installment.amount}
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Render a connector between steps (default style)
     */
    private renderConnector(isDotted: boolean = false) {
        return (
            <div class="installments-connector-container">
                {isDotted ? (
                    <div class="installments-dotted-connector">
                        <div class="installments-connector-dot"></div>
                        <div class="installments-dots-container">
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                        </div>
                        <div class="installments-connector-dot"></div>
                    </div>
                ) : (
                    <div class="installments-simple-connector">
                        <div class="installments-connector-dot"></div>
                        <div class="installments-connector-line"></div>
                        <div class="installments-connector-dot"></div>
                    </div>
                )}
            </div>
        );
    }

    /**
     * Render the modern timeline style with new dots and connecting lines
     * Updated to show dotted line when there are hidden installments
     */
    private renderModernTimeline() {
        const allInstallments = this.installments;
        const installmentsToDisplay = [];
        const hasHiddenInstallments = allInstallments.length > this.maxVisibleInstallments;

        // Handle visibility logic for many installments
        if (allInstallments.length <= this.maxVisibleInstallments) {
            installmentsToDisplay.push(...allInstallments);
        } else {
            // Show first, then second, then last
            installmentsToDisplay.push(
                allInstallments[0],
                allInstallments[1]
            );
            installmentsToDisplay.push(
                allInstallments[allInstallments.length - 1]
            );
        }

        const timelineItems = [];

        // Add full amount as first item (hold)
        const holdItem: Installment = {
            amount: this.amount.toFixed(this.decimalPlaces),
            progressType: 'small',
            label: this.t('Today')
        };
        timelineItems.push(holdItem);

        // Add the filtered installments
        timelineItems.push(...installmentsToDisplay);

        return (
            <div id="paymentProgress"
                 dir={this.locale === 'ar' ? 'rtl' : 'ltr'}
                 class={{
                     'installments': true,
                     'payment-progress': true,
                     'date-timeline': true,
                     'bordered': this.border,
                     'has-hidden-installments': hasHiddenInstallments
                 }}>
                <div class={`date-timeline-container date-timeline-container-${this.locale === 'ar' ? 'rtl' : 'ltr'}`}>
                    {timelineItems.map((installment, index) => {
                        // Adjust label for ellipsis case (same logic as default timeline)
                        const isLastInstallment = index === timelineItems.length - 1 && index > 2;
                        const adjustedInstallment = isLastInstallment && allInstallments.length > 3 ? {
                            ...installment,
                            label: this.t('Installment') + ' ' + allInstallments.length
                        } : installment;

                        return this.renderModernTimelineStep(adjustedInstallment, index === 0);
                    })}
                </div>
                {this.footerMessage && (
                    <div class="date-timeline-footer">
                        {this.footerMessage}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Render the default timeline style with circles and connectors
     */
    private renderDefaultTimeline() {
        const allInstallments = this.installments;
        const installmentsToDisplay = [];

        // Handle visibility logic for many installments
        if (allInstallments.length <= this.maxVisibleInstallments) {
            installmentsToDisplay.push(...allInstallments);
        } else {
            // Show first, then ellipsis, then last
            installmentsToDisplay.push(
                allInstallments[0],
                allInstallments[1]
            );
            installmentsToDisplay.push(
                allInstallments[allInstallments.length - 1]
            );
        }

        return (
            <div id="paymentProgress"
                 style={this.locale === 'ar' ? { direction: 'rtl' } : {}}
                 class={{
                     'installments': true,
                     'payment-progress': true,
                     'bordered': this.border
                 }}>
                {/* Today / Initial Payment */}
                {this.renderInstallmentStep(
                    `${this.amount}`,
                    this.t('Today'),
                    true,
                    false,
                    'empty'
                )}

                {/* Connector between Initial Payment and First Installment */}
                {this.renderConnector()}

                {/* Display the installments */}
                {installmentsToDisplay.map((installment, index) => [
                    this.renderInstallmentStep(
                        installment.amount,
                        this.t('Installment') + ' ' + (index === 2 && allInstallments.length > 3 ? allInstallments.length : index + 1),
                        false,
                        index === installmentsToDisplay.length - 1,
                        installment.progressType
                    ),
                    index < installmentsToDisplay.length - 1 &&
                    this.renderConnector(index === 1 && allInstallments.length > 3)
                ])}
            </div>
        );
    }

    render() {
        // If installments are not enabled, don't render anything
        if (!this.enableInstallments) {
            return null;
        }

        if (this.debug) {
            console.log('[AmwalInstallmentsTimeline] Debug Mode Enabled');
            console.log('[AmwalInstallmentsTimeline] Timeline Style:', this.timelineStyle);
            console.log('[AmwalInstallmentsTimeline] Installments:', this.installments);
            console.log('[AmwalInstallmentsTimeline] Installments Count:', this.installmentsCount);
            console.log('[AmwalInstallmentsTimeline] Amount:', this.amount);
            console.log('[AmwalInstallmentsTimeline] Max Visible Installments:', this.maxVisibleInstallments);
            console.log('[AmwalInstallmentsTimeline] Has Hidden Installments:', this.installments.length > this.maxVisibleInstallments);
            console.log('[AmwalInstallmentsTimeline] Locale:', this.locale);
            console.log('[AmwalInstallmentsTimeline] Enable Border:', this.border);
        }

        // Render based on timeline style
        if (this.timelineStyle === 'simple') {
            return this.renderModernTimeline();
        } else {
            return this.renderDefaultTimeline();
        }
    }
}